import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Pencil, RefreshCcw, Target } from 'lucide-react';
import axios from '../../../axiosConfig';
import { useToast } from '../../../components/ui/Toast';
import { useCurrency } from '../../../contexts/CurrencyContext';
import './BudgetVsActualTab.css';

/* ─── Module-scope helpers (per feedback_no_inline_components) ─── */

function ymOf(d = new Date()) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function ymLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function ymShift(ym, months) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return ymOf(d);
}

const PRESETS = [
  { id: 'this_year',   label: 'This year'   },
  { id: 'last_6',      label: 'Last 6 mo'   },
  { id: 'last_12',     label: 'Last 12 mo'  },
  { id: 'custom',      label: 'Custom'      },
];

// Categories the merchant can budget against. REVENUE = all channels.
// Others map to OrderChannel enum so actual revenue per channel comes
// back from the backend with no client-side math.
const CATEGORIES = [
  { id: 'REVENUE',    label: 'All revenue' },
  { id: 'STOREFRONT', label: 'Storefront'  },
  { id: 'POS',        label: 'POS'         },
  { id: 'WHATSAPP',   label: 'WhatsApp'    },
  { id: 'MANUAL',     label: 'Manual'      },
];

/* ─── Module-scope sub-components ──────────────────────────────── */

function FilterBar({
  category, onCategoryChange,
  preset, onPresetChange,
  from, to, onFromChange, onToChange,
}) {
  return (
    <div className="fin-bud-filterbar">
      <div className="fin-bud-filterbar-row">
        <label className="fin-bud-filterbar-item">
          <span className="fin-bud-filterbar-label">Channel</span>
          <select
            className="fin-bud-filterbar-select"
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            aria-label="Budget category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="fin-bud-filterbar-item">
          <span className="fin-bud-filterbar-label">Range</span>
          <select
            className="fin-bud-filterbar-select"
            value={preset}
            onChange={(e) => onPresetChange(e.target.value)}
            aria-label="Date range"
          >
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </label>
      </div>
      {preset === 'custom' && (
        <div className="fin-bud-filterbar-row fin-bud-filterbar-row--dates">
          <label className="fin-bud-filterbar-item">
            <span className="fin-bud-filterbar-label">From</span>
            <input
              type="month"
              className="fin-bud-filterbar-select"
              value={from}
              onChange={(e) => onFromChange(e.target.value)}
            />
          </label>
          <label className="fin-bud-filterbar-item">
            <span className="fin-bud-filterbar-label">To</span>
            <input
              type="month"
              className="fin-bud-filterbar-select"
              value={to}
              onChange={(e) => onToChange(e.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, hint, tone = 'neutral' }) {
  return (
    <div className={`fin-bud-stat fin-bud-stat--${tone}`}>
      <div className="fin-bud-stat-label">{label}</div>
      <div className="fin-bud-stat-value">{value}</div>
      {hint && <div className="fin-bud-stat-hint">{hint}</div>}
    </div>
  );
}

function BudgetEditor({ monthYear, currentTarget, onSave, saving, fmtMoney }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentTarget ? String(currentTarget) : '');

  // Reset local edit state when the picked month or remote target changes.
  useEffect(() => {
    setEditing(false);
    setValue(currentTarget ? String(currentTarget) : '');
  }, [monthYear, currentTarget]);

  const handleSave = async () => {
    const amt = Number(value);
    if (!Number.isFinite(amt) || amt < 0) return;
    await onSave(monthYear, amt);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="fin-bud-editor-row">
        <div className="fin-bud-editor-value">
          {currentTarget > 0 ? fmtMoney(currentTarget) : <span className="fin-bud-editor-empty">No target set</span>}
        </div>
        <button type="button" className="fin-bud-btn fin-bud-btn--ghost" onClick={() => setEditing(true)}>
          <Pencil size={12} /> {currentTarget > 0 ? 'Edit' : 'Set'}
        </button>
      </div>
    );
  }

  return (
    <div className="fin-bud-editor-row fin-bud-editor-row--editing">
      <span className="fin-bud-editor-prefix" aria-hidden>₦</span>
      <input
        type="number"
        min="0"
        step="100"
        className="fin-bud-editor-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="e.g. 500000"
        autoFocus
      />
      <button type="button" className="fin-bud-btn fin-bud-btn--primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      <button type="button" className="fin-bud-btn fin-bud-btn--ghost" onClick={() => setEditing(false)} disabled={saving}>
        Cancel
      </button>
    </div>
  );
}

/* Compact money formatter for axis labels — turns 12500000 into "₦12.5M". */
function compactMoney(v) {
  const n = Number(v) || 0;
  if (n >= 1e9) return `₦${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `₦${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `₦${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;
  return `₦${n.toFixed(0)}`;
}

/* Inline SVG trend chart — area + line for actual, dashed line for
   target. Y-axis gridlines + value labels (max, mid) give visual
   anchoring; native <title> on each dot reveals the exact value on
   hover. No charting library — keeps the bundle small. */
function TrendSparkline({ rows, fmtMoney }) {
  if (rows.length === 0) return null;
  const W = 600;
  const H = 240;
  const PAD = { top: 18, right: 18, bottom: 28, left: 52 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const rawMax = rows.reduce((m, r) => Math.max(m, r.actual, r.target), 0);
  // Round up to a "nice" axis maximum so labels read cleanly.
  const niceMax = (() => {
    if (rawMax <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const norm = rawMax / pow;
    const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
    return step * pow;
  })();

  const stepX = rows.length > 1 ? innerW / (rows.length - 1) : 0;
  const xy = (i, v) => [PAD.left + i * stepX, PAD.top + innerH - (v / niceMax) * innerH];

  const actualPath = rows
    .map((r, i) => `${i === 0 ? 'M' : 'L'} ${xy(i, r.actual).join(' ')}`)
    .join(' ');
  const areaPath = rows.length > 0
    ? `${actualPath} L ${PAD.left + (rows.length - 1) * stepX} ${PAD.top + innerH} L ${PAD.left} ${PAD.top + innerH} Z`
    : '';
  const hasAnyTarget = rows.some((r) => r.target > 0);
  const targetPath = hasAnyTarget
    ? rows.map((r, i) => `${i === 0 ? 'M' : 'L'} ${xy(i, r.target).join(' ')}`).join(' ')
    : '';
  const last = rows[rows.length - 1];

  // Y-axis gridlines at 0, 50%, 100%
  const yLines = [0, niceMax / 2, niceMax];

  return (
    <div className="fin-bud-trend">
      <div className="fin-bud-trend-head">
        <span className="fin-bud-trend-title">Trend</span>
        <span className="fin-bud-trend-legend">
          <span className="fin-bud-trend-legend-dot fin-bud-trend-legend-dot--actual" /> Actual
          {hasAnyTarget && (
            <>
              <span className="fin-bud-trend-legend-dot fin-bud-trend-legend-dot--target" /> Target
            </>
          )}
        </span>
      </div>
      <svg
        className="fin-bud-trend-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        aria-label={`Monthly actual revenue trend. Latest: ${fmtMoney(last.actual)}`}
      >
        <defs>
          <linearGradient id="fin-bud-spark-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0d7463" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0d7463" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Y-axis gridlines + labels */}
        {yLines.map((v, idx) => {
          const y = PAD.top + innerH - (v / niceMax) * innerH;
          return (
            <g key={idx}>
              <line
                x1={PAD.left} x2={W - PAD.right}
                y1={y} y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray={idx === 0 ? '0' : '2 3'}
              />
              <text x={PAD.left - 6} y={y + 3} fontSize="9" fill="#9ca3af" textAnchor="end">
                {compactMoney(v)}
              </text>
            </g>
          );
        })}

        {areaPath && <path d={areaPath} fill="url(#fin-bud-spark-grad)" />}
        {actualPath && <path d={actualPath} fill="none" stroke="#0d7463" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {targetPath && <path d={targetPath} fill="none" stroke="#6b7280" strokeWidth="1.5" strokeDasharray="5 3" />}

        {/* Data points — circle with tooltip via <title>. Hovering on
            desktop reveals the precise value; touch users tap the row
            in the table below. */}
        {rows.map((r, i) => {
          const [cx, cy] = xy(i, r.actual);
          const tip = `${ymLabel(r.monthYear)} — Actual ${fmtMoney(r.actual)}${r.target > 0 ? ` · Target ${fmtMoney(r.target)}` : ''}`;
          return (
            <g key={r.monthYear}>
              <circle cx={cx} cy={cy} r="3" fill="#ffffff" stroke="#0d7463" strokeWidth="2">
                <title>{tip}</title>
              </circle>
            </g>
          );
        })}

        {/* X-axis: first / middle / last. Skip middle if just 1-2 rows. */}
        {rows.length > 0 && (
          <text x={PAD.left} y={H - 8} fontSize="10" fill="#9ca3af">{ymLabel(rows[0].monthYear)}</text>
        )}
        {rows.length > 2 && (
          <text
            x={PAD.left + Math.floor(rows.length / 2) * stepX}
            y={H - 8}
            fontSize="10" fill="#9ca3af" textAnchor="middle"
          >
            {ymLabel(rows[Math.floor(rows.length / 2)].monthYear)}
          </text>
        )}
        {rows.length > 1 && (
          <text x={W - PAD.right} y={H - 8} fontSize="10" fill="#9ca3af" textAnchor="end">
            {ymLabel(rows[rows.length - 1].monthYear)}
          </text>
        )}
      </svg>
    </div>
  );
}

function PeriodTable({ rows, fmtMoney }) {
  if (rows.length === 0) {
    return (
      <div className="fin-bud-empty">
        <div className="fin-bud-empty-title">Pick a wider range</div>
        <div className="fin-bud-empty-sub">No months fell in the selected window.</div>
      </div>
    );
  }
  return (
    <div className="fin-bud-table-wrap">
      <table className="fin-bud-table">
        <colgroup>
          <col />
          <col style={{ width: 140 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 90 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Period</th>
            <th className="fin-bud-th-num">Target</th>
            <th className="fin-bud-th-num">Actual</th>
            <th className="fin-bud-th-num">Variance</th>
            <th className="fin-bud-th-num">%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const onTrack = r.target > 0 && r.actual >= r.target;
            return (
              <tr key={r.monthYear}>
                <td className="fin-bud-td-label">{ymLabel(r.monthYear)}</td>
                <td className="fin-bud-td-num" data-label="Target">
                  {r.target > 0 ? fmtMoney(r.target) : <span className="fin-bud-td-muted">—</span>}
                </td>
                <td className="fin-bud-td-num" data-label="Actual">{fmtMoney(r.actual)}</td>
                <td
                  className={`fin-bud-td-num ${r.target > 0 ? (onTrack ? 'fin-bud-td-pos' : 'fin-bud-td-neg') : ''}`}
                  data-label="Variance"
                >
                  {r.target > 0 ? `${r.variance >= 0 ? '+' : ''}${fmtMoney(r.variance)}` : <span className="fin-bud-td-muted">—</span>}
                </td>
                <td
                  className={`fin-bud-td-num ${r.target > 0 ? (onTrack ? 'fin-bud-td-pos' : 'fin-bud-td-neg') : ''}`}
                  data-label="%"
                >
                  {r.variancePct != null ? `${r.variancePct >= 0 ? '+' : ''}${r.variancePct}%` : <span className="fin-bud-td-muted">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */

export default function BudgetVsActualTab() {
  const toast = useToast();
  const { formatMoney } = useCurrency();

  const thisYM = ymOf(new Date());
  const [preset, setPreset] = useState('this_year');
  const [from, setFrom] = useState(`${new Date().getUTCFullYear()}-01`);
  const [to, setTo] = useState(thisYM);
  const [category, setCategory] = useState('REVENUE');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  // Month the inline editor currently targets (defaults to current month).
  const [editorYM, setEditorYM] = useState(thisYM);

  useEffect(() => {
    if (preset === 'this_year') {
      setFrom(`${new Date().getUTCFullYear()}-01`);
      setTo(thisYM);
    } else if (preset === 'last_6') {
      setFrom(ymShift(thisYM, -5));
      setTo(thisYM);
    } else if (preset === 'last_12') {
      setFrom(ymShift(thisYM, -11));
      setTo(thisYM);
    }
  }, [preset, thisYM]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/finance/budget-vs-actual', { params: { from, to, category } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load budget vs actual');
    } finally {
      setLoading(false);
    }
  }, [from, to, category, toast]);

  useEffect(() => { reload(); }, [reload]);

  const fmtMoney = useCallback((v) => formatMoney(v ?? 0), [formatMoney]);

  const items = data?.items || [];
  const totals = data?.totals || { target: 0, actual: 0, variance: 0, variancePct: null };
  const currentEditorTarget = useMemo(() => {
    const row = items.find((r) => r.monthYear === editorYM);
    return row ? Number(row.target) : 0;
  }, [items, editorYM]);

  const onTrackTone = totals.target > 0 && totals.actual >= totals.target ? 'positive' : (totals.target > 0 ? 'negative' : 'neutral');

  const handleCsv = useCallback(async () => {
    try {
      const resp = await axios.get('/finance/budget-vs-actual.csv', {
        params: { from, to, category }, responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-vs-actual_${category.toLowerCase()}_${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to download CSV');
    }
  }, [from, to, category, toast]);

  const saveTarget = useCallback(async (monthYear, amount) => {
    setSaving(true);
    try {
      await axios.post('/finance/budgets', { monthYear, amount, category });
      toast.success(`Saved ${ymLabel(monthYear)} target`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save target');
    } finally {
      setSaving(false);
    }
  }, [reload, toast, category]);

  return (
    <div className="fin-bud-tab">
      <header className="fin-bud-header">
        <div className="fin-bud-header-text">
          <h2 className="fin-bud-title">Budget vs actual</h2>
          <p className="fin-bud-subtitle">
            Set a monthly revenue target and watch your sales track against it. Variance turns red when you fall behind, green when you beat it.
          </p>
        </div>
        <div className="fin-bud-header-actions">
          <button type="button" className="fin-bud-btn fin-bud-btn--ghost" onClick={reload} disabled={loading}>
            <RefreshCcw size={14} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="fin-bud-btn fin-bud-btn--primary"
            onClick={handleCsv}
            disabled={loading || items.length === 0}
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </header>

      <FilterBar
        category={category}
        onCategoryChange={setCategory}
        preset={preset}
        onPresetChange={setPreset}
        from={from}
        to={to}
        onFromChange={(v) => { setPreset('custom'); setFrom(v); }}
        onToChange={(v) => { setPreset('custom'); setTo(v); }}
      />

      {category === 'POS' && (
        <div className="fin-bud-note">
          <strong>Heads up:</strong> POS revenue only counts orders marked
          <em> paid</em>. Bank-transfer sales sit unpaid until you confirm
          payment. If this number looks low,{' '}
          <a href="/orders">check your Orders page</a> for pending POS
          orders waiting to be confirmed.
        </div>
      )}

      <section className="fin-bud-stats">
        <StatCard
          label="Total target"
          value={loading ? '—' : fmtMoney(totals.target)}
          hint={`${items.length} month${items.length === 1 ? '' : 's'}`}
        />
        <StatCard
          label="Actual revenue"
          value={loading ? '—' : fmtMoney(totals.actual)}
          hint="Sum of paid order subtotals"
        />
        <StatCard
          label="Variance"
          tone={onTrackTone}
          value={loading
            ? '—'
            : totals.target > 0
              ? `${totals.variance >= 0 ? '+' : ''}${fmtMoney(totals.variance)}`
              : '—'}
          hint={loading
            ? null
            : totals.target > 0 && totals.variancePct != null
              ? `${totals.variancePct >= 0 ? '+' : ''}${totals.variancePct}% vs target`
              : 'Set a target to see variance'}
        />
      </section>

      <div className="fin-bud-editor-shell">
        <div className="fin-bud-editor-head">
          <Target size={14} className="fin-bud-editor-icon" aria-hidden />
          <span className="fin-bud-editor-title">Monthly target</span>
          <select
            value={editorYM}
            onChange={(e) => setEditorYM(e.target.value)}
            className="fin-bud-editor-month-select"
            aria-label="Pick month to edit"
          >
            {items.length > 0
              ? items.map((it) => (
                  <option key={it.monthYear} value={it.monthYear}>{ymLabel(it.monthYear)}</option>
                ))
              : <option value={thisYM}>{ymLabel(thisYM)}</option>}
          </select>
        </div>
        <BudgetEditor
          monthYear={editorYM}
          currentTarget={currentEditorTarget}
          onSave={saveTarget}
          saving={saving}
          fmtMoney={fmtMoney}
        />
      </div>

      <TrendSparkline rows={items} fmtMoney={fmtMoney} />
      <PeriodTable rows={items} fmtMoney={fmtMoney} />
    </div>
  );
}
